const PAVO_API_URL = process.env.REACT_APP_GTS_PAVO_API_URL || 'http://localhost:5011';

interface PavoDevice {
  deviceCode: string;
  companyId: string;
  branchId?: string;
  deviceMode?: string;
  deviceName: string;
  isActive: boolean;
  isOnline: boolean;
  linkedScreenId?: number;
  linkedScreenType?: string;
  pavoNumber?: number;
  deviceModel?: string;
  appVersion?: string;
  status?: string;
  lastSeen?: string;
  companyName?: string;
  branchName?: string;
}

interface PavoPaymentRequest {
  requestId: string;
  deviceCode: string;
  totalAmount: number;
  itemsJson?: string;
  sourceType: string;
  sourceReference?: string;
  status: string;
  companyId?: string;
  branchId?: string;
  createdAt?: string;
  completedAt?: string;
  paymentResultJson?: string;
  pavoSaleNumber?: string;
  errorMessage?: string;
  orderId?: string;
  orderSource?: string;
  tableId?: number;
}

const getHeaders = (companyId?: string): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (companyId) headers['X-Company-Id'] = companyId;
  return headers;
};

const mapPaymentRequest = (p: any): PavoPaymentRequest => ({
  requestId: p.RequestId ?? p.requestId ?? '',
  deviceCode: p.DeviceCode ?? p.deviceCode ?? '',
  totalAmount: p.TotalAmount ?? p.totalAmount ?? 0,
  itemsJson: p.ItemsJson ?? p.itemsJson,
  sourceType: p.SourceType ?? p.sourceType ?? '',
  sourceReference: p.SourceReference ?? p.sourceReference,
  status: p.Status ?? p.status ?? 'pending',
  companyId: p.CompanyId ?? p.companyId,
  branchId: p.BranchId ?? p.branchId,
  createdAt: p.CreatedAt ?? p.createdAt,
  completedAt: p.CompletedAt ?? p.completedAt,
  paymentResultJson: p.PaymentResultJson ?? p.paymentResultJson,
  pavoSaleNumber: p.PavoSaleNumber ?? p.pavoSaleNumber,
  errorMessage: p.ErrorMessage ?? p.errorMessage,
  orderId: p.OrderId ?? p.orderId,
  orderSource: p.OrderSource ?? p.orderSource,
  tableId: p.TableId ?? p.tableId,
});

const mapDevice = (d: any): PavoDevice => ({
  deviceCode: d.DeviceCode ?? d.deviceCode ?? '',
  companyId: d.CompanyId ?? d.companyId ?? '',
  branchId: d.BranchId ?? d.branchId,
  deviceMode: d.DeviceMode ?? d.deviceMode,
  deviceName: d.DeviceName ?? d.deviceName ?? '',
  isActive: d.IsActive ?? d.isActive ?? true,
  isOnline: d.IsOnline ?? d.isOnline ?? false,
  linkedScreenId: d.LinkedScreenId ?? d.linkedScreenId,
  linkedScreenType: d.LinkedScreenType ?? d.linkedScreenType,
  pavoNumber: d.PavoNumber ?? d.pavoNumber,
  deviceModel: d.DeviceModel ?? d.deviceModel,
  appVersion: d.AppVersion ?? d.appVersion,
  status: d.Status ?? d.status,
  lastSeen: d.LastSeen ?? d.lastSeen,
  companyName: d.CompanyName ?? d.companyName,
  branchName: d.BranchName ?? d.branchName,
});

export interface AddPavoDeviceResult {
  device: PavoDevice;
  deviceCode: string;
  /**
   * TEK SEFERLIK. Sadece cihaz ekleme (veya ilk aktivasyon) cevabinda doner.
   * null ise backend secret dondurmemistir (ornegin eski API).
   */
  deviceSecret: string | null;
}

export const pavoApiService = {
  async addPavoDevice(
    companyId: string,
    branchId: string,
    deviceMode: string,
    deviceName: string,
    linkedScreenId?: number,
    linkedScreenType?: string,
  ): Promise<AddPavoDeviceResult> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoDevice/add`, {
      method: 'POST',
      headers: getHeaders(companyId),
      body: JSON.stringify({
        CompanyId: companyId,
        BranchId: branchId,
        DeviceMode: deviceMode,
        DeviceName: deviceName,
        LinkedScreenId: linkedScreenId,
        LinkedScreenType: linkedScreenType,
      }),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Cihaz eklenemedi');
    return {
      device: mapDevice(data.Device),
      deviceCode: data.DeviceCode ?? data.deviceCode ?? '',
      deviceSecret: data.DeviceSecret ?? data.deviceSecret ?? null,
    };
  },

  /**
   * Bir QuickSaleScreen veya RestaurantScreen'e bagli Pavo cihazlarini listeler.
   */
  async getDevicesByScreen(
    companyId: string,
    screenId: number,
    screenType: 'quicksale' | 'restaurant' = 'quicksale',
  ): Promise<PavoDevice[]> {
    const response = await fetch(
      `${PAVO_API_URL}/api/PavoDevice/list-by-screen?screenId=${screenId}&screenType=${encodeURIComponent(screenType)}`,
      { headers: getHeaders(companyId) },
    );
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Devices || []).map(mapDevice);
  },

  async getPavoDevices(companyId: string, branchId?: string): Promise<PavoDevice[]> {
    const url = branchId
      ? `${PAVO_API_URL}/api/PavoDevice/list/${companyId}?branchId=${encodeURIComponent(branchId)}`
      : `${PAVO_API_URL}/api/PavoDevice/list/${companyId}`;
    const response = await fetch(url, {
      headers: getHeaders(companyId),
    });
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Devices || []).map(mapDevice);
  },

  async getPavoDevice(deviceCode: string): Promise<PavoDevice> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoDevice/${deviceCode}`, {
      headers: getHeaders(),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Cihaz bulunamadı');
    return mapDevice(data.Device);
  },

  async updatePavoDevice(deviceCode: string, updates: Partial<PavoDevice>): Promise<PavoDevice> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoDevice/update/${deviceCode}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Cihaz güncellenemedi');
    return mapDevice(data.Device);
  },

  async deletePavoDevice(deviceCode: string, companyId: string): Promise<void> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoDevice/delete/${deviceCode}`, {
      method: 'POST',
      headers: getHeaders(companyId),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Cihaz silinemedi');
  },

  async getOnlineDevices(companyId: string, branchId?: string): Promise<PavoDevice[]> {
    const url = branchId
      ? `${PAVO_API_URL}/api/PavoDevice/online/${companyId}?branchId=${encodeURIComponent(branchId)}`
      : `${PAVO_API_URL}/api/PavoDevice/online/${companyId}`;
    const response = await fetch(url, {
      headers: getHeaders(companyId),
    });
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Devices || []).map(mapDevice);
  },

  async createPaymentRequest(
    companyId: string,
    branchId: string,
    deviceCode: string,
    totalAmount: number,
    items: any[],
    sourceType: string,
    sourceReference?: string,
    tableId?: number,
    extraFields?: {
      orderSource?: string;
      orderId?: string;
      screenId?: number;
      accountId?: number;
      accountName?: string;
      /**
       * Tam QuickSalePosWithInvoiceRequestDto payload'u (object).
       * PavoPay odemesi tamamlandiginda server direkt bu payload ile
       * /api/QuickSaleWithInvoice/process-pos-with-invoice endpoint'ini cagirir.
       * Boylece web kapali olsa bile fatura/stok/cari akisi islenir.
       */
      invoicePayload?: any;
    },
  ): Promise<PavoPaymentRequest> {
    const invoicePayloadJson = extraFields?.invoicePayload
      ? JSON.stringify(extraFields.invoicePayload)
      : undefined;

    const response = await fetch(`${PAVO_API_URL}/api/PavoPayment/create-request`, {
      method: 'POST',
      headers: getHeaders(companyId),
      body: JSON.stringify({
        DeviceCode: deviceCode,
        TotalAmount: totalAmount,
        ItemsJson: JSON.stringify(items),
        SourceType: sourceType,
        SourceReference: sourceReference,
        CompanyId: companyId,
        BranchId: branchId,
        ...(tableId ? { TableId: tableId } : {}),
        ...(extraFields?.orderSource ? { OrderSource: extraFields.orderSource } : {}),
        ...(extraFields?.orderId ? { OrderId: extraFields.orderId } : {}),
        ...(extraFields?.screenId ? { ScreenId: extraFields.screenId } : {}),
        ...(extraFields?.accountId ? { AccountId: extraFields.accountId } : {}),
        ...(extraFields?.accountName ? { AccountName: extraFields.accountName } : {}),
        ...(invoicePayloadJson ? { InvoicePayloadJson: invoicePayloadJson } : {}),
      }),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Ödeme talebi oluşturulamadı');
    return mapPaymentRequest(data.Payment);
  },

  async getPaymentRequest(requestId: string): Promise<PavoPaymentRequest> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoPayment/${requestId}`, {
      headers: getHeaders(),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Ödeme talebi bulunamadı');
    return mapPaymentRequest(data.Payment);
  },

  async getPendingPayments(deviceCode: string): Promise<PavoPaymentRequest[]> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoPayment/pending/${deviceCode}`, {
      headers: getHeaders(),
    });
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Payments || []).map(mapPaymentRequest);
  },

  async cancelPayment(requestId: string): Promise<PavoPaymentRequest> {
    const response = await fetch(`${PAVO_API_URL}/api/PavoPayment/cancel/${requestId}`, {
      method: 'POST',
      headers: getHeaders(),
    });
    const data = await response.json();
    if (!data.Success) throw new Error(data.Message || 'Ödeme iptal edilemedi');
    return mapPaymentRequest(data.Payment);
  },

  async getActivePayments(companyId: string, branchId?: string): Promise<PavoPaymentRequest[]> {
    const url = branchId
      ? `${PAVO_API_URL}/api/PavoPayment/active/${companyId}?branchId=${encodeURIComponent(branchId)}`
      : `${PAVO_API_URL}/api/PavoPayment/active/${companyId}`;
    const response = await fetch(url, {
      headers: getHeaders(companyId),
    });
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Payments || []).map(mapPaymentRequest);
  },

  async getPaymentHistory(
    companyId: string,
    startDate: string,
    endDate: string,
  ): Promise<PavoPaymentRequest[]> {
    const response = await fetch(
      `${PAVO_API_URL}/api/PavoPayment/history?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`,
      { headers: getHeaders(companyId) },
    );
    const data = await response.json();
    if (!data.Success) return [];
    return (data.Payments || []).map(mapPaymentRequest);
  },
};

export type { PavoDevice, PavoPaymentRequest };
export default pavoApiService;
